import React, {
  useMemo,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
} from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  // Eye
  const rowClicked = useCallback(
    (row) => {
      console.log('row', row);
      const wasteTransactionValue = row?.wasteTransactionType?.value;
      const { masterId } = row;

      if (!masterId || !wasteTransactionValue) {
        return;
      }

      switch (wasteTransactionValue) {
        case 1:
          // collection
          history.push({
            pathname: generatePath(getRouteByName('Collection'), { id: masterId }),
            state: {
              pathname: location.pathname,
              ...location.state,
            },
          });
          break; // <-- missing break
        case 2:
          // internalTransfer
          history.push({
            pathname: generatePath(getRouteByName('ITran'), { id: masterId }),
            state: {
              pathname: location.pathname,
              ...location.state,
            },
          });
          break; // <-- missing break
        case 3:
          // production
          history.push({
            pathname: generatePath(getRouteByName('Production'), { id: masterId }),
            state: {
              pathname: location.pathname,
              ...location.state,
            },
          });
          break; // <-- missing break
        default:
          console.warn('Unhandled wasteTransactionValue:', wasteTransactionValue);
      }
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'masterId',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'masterId',
        orderByField: 'masterId',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'transactionAa',
        header: t('Αα'),
        columnName: t('Αα'),
        accessorKey: 'transactionAa',
        orderByField: 'transactionAa',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },
      {
        id: 'transactionDate',
        header: t('Ημ/νία Κίνησης'),
        columnName: t('Ημ/νία Κίνησης'),
        accessorKey: 'transactionDate',
        orderByField: 'transactionDate',
        ...dateColumn(t),
        size: 15,
      },
      {
        id: 'transactionCode',
        header: t('Κωδικός Κίνησης'),
        columnName: t('Κωδικός Κίνησης'),
        accessorKey: 'transactionCode',
        orderByField: 'transactionCode',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },
      {
        id: 'wasteTransactionTypeId',
        header: t('Τύπος Κίνησης'),
        columnName: t('Τύπος Κίνησης'),
        accessorKey: 'wasteTransactionType.name',
        isSortable: false,
        cell: TableCellV8,
        size: 40,
      },

      {
        id: 'materialName',
        header: t('Απόβλητο'),
        columnName: t('Απόβλητο'),
        accessorKey: 'materialName',
        orderByField: 'materialName',
        cell: TableCellV8,
        size: 25,
      },
      {
        id: 'sourceSiteName',
        header: t('Προέλευση Αποβλήτων'),
        columnName: t('Προέλευση Αποβλήτων'),
        accessorKey: 'sourceSiteName',
        orderByField: 'sourceSiteName',
        cell: TableCellV8,
        size: 25,
      },
      {
        id: 'destinationSiteName',
        header: t('Προορισμός Αποβλήτων'),
        columnName: t('Προορισμός Αποβλήτων'),
        accessorKey: 'destinationSiteName',
        orderByField: 'destinationSiteName',
        cell: TableCellV8,
        size: 25,
      },
      {
        id: 'inputWeight',
        header: t('Εισαγωγή (Kg)'),
        columnName: t('Εισαγωγή (Kg)'),
        accessorKey: 'inputWeight',
        orderByField: 'inputWeight',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'number' },
      },
      {
        id: 'outputWeight',
        header: t('Εξαγωγή (Kg)'),
        columnName: t('Εξαγωγή (Kg)'),
        accessorKey: 'outputWeight',
        orderByField: 'outputWeight',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'number' },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));
  // useEffect(() => {
  //   // console.log('$$$data', data);
  // }, [data]);
  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
